import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Grid } from 'semantic-ui-react';
import { pageTitle, roles, userAllowed, NetworkType } from '../../helpers/utils';
import { onLoad, onUnload } from '../../actions/pages';
import { kpisView, kpisThresoldModify, kpiThresoldDefault } from '../../actions/performances';
import ErrorMessage from '../ErrorMessage';
import LoadingPage from '../LoadingPage';
import dounlinkThroughPutImg from '../../semantic-ui/site/assets/download-blk.png';
import uploadThroughPutImg from '../../semantic-ui/site/assets/upload-blk.png';
import combined_throughput from '../../semantic-ui/site/assets/combined_throughput.svg';
import routerBlk from '../../semantic-ui/site/assets/router-blk.png';
import remindMeBlk from '../../semantic-ui/site/assets/remind-me-blk.png';
import attachFailure from '../../semantic-ui/site/assets/attach_failure.png';
import { OverallWidget } from './overallWidget';
import { decorateOverallKPI } from '../../helpers/performanceUtil';
import { SOLUTION_TYPE } from '../../helpers/const';

export const getInfo = (OverallKPIType) => {
    let ret = {};
    switch (OverallKPIType) {
        case 'ATTACHED_DEVICES':
            return (ret = { name: 'Attached devices', image: routerBlk, maxVal: 5000 });

        case 'COMBINED_THROUGHPUT':
            return (ret = { name: 'Combined throughput', image: combined_throughput });

        case 'DOWNLINK_THROUGHPUT':
            return (ret = { name: 'Core downlink throughput', image: dounlinkThroughPutImg, maxVal: 8 * 1000 });

        case 'UPLINK_THROUGHPUT':
            return (ret = { name: 'Core uplink throughput', image: uploadThroughPutImg, maxVal: 4 * 1000 });

        case 'ATTACH_FAILURE_RATIO':
            return (ret = { name: 'Attach failure', image: attachFailure });

        case 'SERVICE_REQUEST_FAILURE_RATIO':
            return (ret = { name: 'Service request failure', image: remindMeBlk });

        default:
            return ret;
    }
};

const KPIViewPage = (props) => {
    const { reload, setReload } = props;
    const { enterpriseId, locationId, roles: userRoles, isIBC, solutionType, isFLEX } = useSelector((state) => state.auth);

    const isManageAttachedDevices = userAllowed(userRoles, [roles.MANAGE_ATTACHED_DEVICES]);
    const isManageDLThroughput = userAllowed(userRoles, [roles.MANAGE_DL_THROUGHPUT], false);
    const isManageULThroughput = userAllowed(userRoles, [roles.MANAGE_UL_THROUGHPUT], false);
    const isManageSuccess = userAllowed(userRoles, [roles.MANAGE_ATTACH_SUCCESS]);

    const isAccessibilityAllowed = isFLEX && userAllowed(userRoles, [roles.KPI_ACCESSIBILITY]);
    const isMobilityAllowed = isFLEX && userAllowed(userRoles, [roles.KPI_MOBILITY]);
    const isRetainabilityAllowed = isFLEX && userAllowed(userRoles, [roles.KPI_RETAINABILITY]);

    const reduxDispatch = useDispatch();
    const eventNotification = useSelector((state) => state.websocket.event);
    const [loading, setLoading] = useState(true);

    const [errorMessage, setErrorMessage] = useState();

    const [kpiDataView, setKpiData] = useState([]);
    const [kpiDefaultData, setKpiDefaultData] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState();
    const [totalDeviceCount, setTotalDeviceCount] = useState();

    // const [deleteKpiProfile, setDeleteKpiProfile] = useState();

    const kpisViewInfo = (payload) => reduxDispatch(kpisView(payload));
    const kpiThresoldUpdate = (json) => reduxDispatch(kpisThresoldModify(json));
    const kpiThresoldDefaultValue = (json) => reduxDispatch(kpiThresoldDefault(json));
    // const generateCsv = (json) => reduxDispatch(generateReportCsv(json));
    const isEP5G = useMemo(() => solutionType === SOLUTION_TYPE.EP5G, [solutionType]);
    const isEricssonSolution = useMemo(() => [SOLUTION_TYPE.EPG, SOLUTION_TYPE.EP5G].includes(solutionType), [solutionType]);
    const isNDACSolution = useMemo(() => solutionType === SOLUTION_TYPE.NDAC, [solutionType]);

    const fetchData = async (type) => {
        try {
            const payload = {};
            payload.enterpriseId = enterpriseId;
            payload.locationId = locationId;
            setLoading(true);

            if (type === 'RELOAD_REFRESH') {
                await reduxDispatch(kpisViewInfo(payload));
            } else {
                setLoading(true);

                const { responseStatus, responseData, defaultData, kpiMetricLastUpdateDate, totalDeviceCount } = await kpisViewInfo(payload);
                if (responseStatus === 200) {
                    setKpiData(responseData);
                    setKpiDefaultData(defaultData);
                    reduxDispatch({ type: 'KPI_OVERVIEW_DATA', payload: { kpiOverviewData: responseData, kpiMetricLastUpdateDate } });
                    setLoading(false);
                    setTotalDeviceCount(totalDeviceCount);
                }
            }
        } catch ({ message }) {
            setErrorMessage(message);
        } finally {
            setLoading(false);
        }
    };

    const submitModifyKpi = async (payload) => {
        try {
            const { responseStatus, responseData } = await kpiThresoldUpdate(payload);
            if (responseStatus === 200) {
                const val = responseData.find((thresold) => thresold.kpiType === payload.kpiType);
                setKpiData(responseData);
                reduxDispatch({ type: 'KPI_OVERVIEW_DATA', payload: { kpiOverviewData: responseData } });
                setLoading(false);
            }
        } catch ({ errorMessage: errors }) {
            setErrorMessage(errors);
        } finally {
            setLoading(false);
        }
    };

    /**
     *
     */
    useEffect(() => {
        fetchData();
        // refresh page every 15 mins
        setRefreshInterval(setInterval(fetchData, 15 * 60 * 1000));
        return () => {
            clearInterval(refreshInterval);
            setRefreshInterval();
        };
    }, []);

    /**
     * Handles:
     *   componentDidMount
     *   componentWillUnmount
     *
     */
    useEffect(() => {
        // componentDidMount
        reduxDispatch(onLoad('KPI_VIEW'));
        document.title = pageTitle('KPIs');
        //

        // componentWillUnmount
        return () => {
            reduxDispatch(onUnload('KPI_VIEW'));
        };
    }, [submitModifyKpi]);

    /**
     * When we initially load the page there might be no data in cache.  We are forced to show loader until
     * the server side sends a DATA_SYNC notification, then we load the data.
     */
    useEffect(() => {
        if (eventNotification) {
            const { notificationType, message } = eventNotification;

            if (notificationType === 'DATA_SYNC' && message === 'SUCCESS') {
                fetchData('LOAD');
            }
        }
    }, [eventNotification]);

    useEffect(() => {
        if (reload) {
            fetchData();
            setReload(false);
        }
    }, [reload]);

    const errorMessageDismiss = () => setErrorMessage();

    //attach device
    const attachedDevicesKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACHED_DEVICES', false);
    const enabledCount = attachedDevicesKPI?.value ? Number(attachedDevicesKPI?.value) : 0;
    const deviceCount = {
        enabledCount,
        offlineCount: totalDeviceCount - enabledCount,
        totalCount: totalDeviceCount
    };

    //attach success ratio
    const attachedSuccessRatioKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACH_SUCCESS', false);
    const successCount = attachedSuccessRatioKPI?.value ? Number(attachedSuccessRatioKPI?.value) : 0;
    const successDeviceCount = {
        successCount,
        failuresCount: totalDeviceCount - successCount,
        totalCount: `${Math.round(((successCount * 100) / totalDeviceCount) * 10) / 10}%`
    };

    // accesibility
    const accesibilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_ACCESSIBILITY', true);

    // mobililty
    const mobililtyKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_MOBILITY', true);

    // retainability
    const retainabilityKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ENB_RETAINABILITY', true);

    //attach rate
    const attachRateKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, 'ATTACH_SUCCESS', true);

    // up link
    const upLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, `${isIBC ? 'UL_CELL_THROUGHPUT' : 'UPLINK_THROUGHPUT'}`, false);

    // down link
    const downLinkKPI = decorateOverallKPI(kpiDataView, kpiDefaultData, `${isIBC ? 'DL_CELL_THROUGHPUT' : 'DOWNLINK_THROUGHPUT'}`, false);

    //combinedThroughput KPI:
    const throughputValues =
        upLinkKPI &&
        downLinkKPI &&
        [downLinkKPI, upLinkKPI].map((item) =>
            item?.value >= 1000 ? Math.round((item?.value / 1000) * 10) / 10 + 'k' : Math.round(item?.value * 10) / 10
        );

    return (
        <>
            {loading ? (
                <LoadingPage />
            ) : (
                <>
                    <Grid.Column computer="11" mobile="12">
                        {errorMessage && (
                            <Grid.Row>
                                <Grid.Column computer="11" mobile="12">
                                    <ErrorMessage onDismiss={errorMessageDismiss} errors={errorMessage} />
                                </Grid.Column>
                            </Grid.Row>
                        )}

                        <Grid.Row computer="12" mobile="12" className="kpi-core-grid">
                            <Card.Group itemsPerRow={3}>
                                {isAccessibilityAllowed && accesibilityKPI && isEricssonSolution && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={'Accessibility'}
                                            KPIValue={accesibilityKPI?.value}
                                            thresholds={accesibilityKPI?.thresholds}
                                            kpiType={accesibilityKPI?.kpiType}
                                            defaultThresholds={accesibilityKPI?.defaultThresholds}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isMobilityAllowed && mobililtyKPI && isEricssonSolution && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={'Mobility'}
                                            KPIValue={mobililtyKPI?.value}
                                            thresholds={mobililtyKPI?.thresholds}
                                            kpiType={mobililtyKPI?.kpiType}
                                            defaultThresholds={mobililtyKPI?.defaultThresholds}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isRetainabilityAllowed && retainabilityKPI && isEricssonSolution && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={'Retainability'}
                                            KPIValue={retainabilityKPI?.value}
                                            thresholds={retainabilityKPI?.thresholds}
                                            kpiType={retainabilityKPI?.kpiType}
                                            defaultThresholds={retainabilityKPI?.defaultThresholds}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isManageAttachedDevices && attachedDevicesKPI && !isIBC && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={'Attached devices'}
                                            thresholds={attachedDevicesKPI?.thresholds}
                                            kpiType={attachedDevicesKPI?.kpiType}
                                            defaultThresholds={attachedDevicesKPI?.defaultThresholds}
                                            deviceCount={deviceCount}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isManageSuccess && attachRateKPI && !isIBC && !isEP5G && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={'Attach success rate'}
                                            KPIValue={attachRateKPI?.value}
                                            thresholds={attachRateKPI?.thresholds}
                                            kpiType={attachRateKPI?.kpiType}
                                            defaultThresholds={attachRateKPI?.defaultThresholds}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isManageAttachedDevices && attachedSuccessRatioKPI && isIBC && !isEP5G && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={'Attached success ratio'}
                                            thresholds={attachedSuccessRatioKPI?.thresholds}
                                            kpiType={attachedSuccessRatioKPI?.kpiType}
                                            defaultThresholds={attachedSuccessRatioKPI?.defaultThresholds}
                                            deviceCount={successDeviceCount}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isManageULThroughput && upLinkKPI && isIBC && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={'Uplink throughput'}
                                            KPIValue={upLinkKPI.value}
                                            thresholds={upLinkKPI?.thresholds}
                                            kpiType={upLinkKPI?.kpiType}
                                            defaultThresholds={upLinkKPI?.defaultThresholds}
                                            isIBC
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isManageDLThroughput && downLinkKPI && isIBC && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            KPIValue={downLinkKPI.value}
                                            title={'Downlink throughput'}
                                            thresholds={downLinkKPI?.thresholds}
                                            kpiType={downLinkKPI?.kpiType}
                                            defaultThresholds={downLinkKPI?.defaultThresholds}
                                            isIBC
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isManageDLThroughput && isManageULThroughput && throughputValues && !isIBC && !isNDACSolution && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title={(isEP5G ? 'RAN' : 'Core') + ' combined throughput'}
                                            throughputValues={throughputValues}
                                            isEP5G={isEP5G}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                                {isManageDLThroughput && isManageULThroughput && throughputValues && !isIBC && isNDACSolution && (
                                    <Card raised={true}>
                                        <OverallWidget
                                            loading={loading}
                                            enterpriseId={enterpriseId}
                                            locationId={locationId}
                                            title="Core throughput"
                                            throughputValues={throughputValues}
                                            isEP5G={isEP5G}
                                            onSubmit={submitModifyKpi}></OverallWidget>
                                    </Card>
                                )}
                            </Card.Group>
                        </Grid.Row>
                    </Grid.Column>
                </>
            )}
        </>
    );
};

export default KPIViewPage;
